/* eslint-disable no-unused-vars */
import React, { useEffect, useState, useRef } from 'react';
import './UserDetailsComponent.css';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, CircularProgress, Grid, TextField } from '@mui/material';
import { api } from '../../../APIs/api';
import { API_URL, BASE_URL } from '../../../APIs/endpoint';
import { toast } from 'react-toastify';
import moment from 'moment';
import ReCAPTCHA from 'react-google-recaptcha';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import { FormControl, InputLabel, Select, MenuItem, FormHelperText } from '@mui/material';

// Styles for dropdown and list items
const dropdownStyles = {
  border: '1px solid #ccc',
  maxHeight: '150px',
  overflowY: 'auto',
  position: 'absolute',
  background: 'white',
  width: '100%',
  zIndex: 1000,
  marginTop: '5px',
  padding: 0
};

const listItemStyles = {
  padding: '10px',
  cursor: 'pointer',
  borderBottom: '1px solid #ddd',
  listStyleType: 'none'
};

const UserDetailsModal = () => {
  const navigate = useNavigate();
  const [countryData, setCountryData] = useState([]);
  const [filteredCities, setFilteredCities] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [formValues, setFormValues] = useState({
    name: '',
    nationality: '',
    country: '',
    email: '',
    phoneNumber: ''
  });
  const [errors, setErrors] = useState({
    name: '',
    nationality: '',
    country: '',
    email: '',
    phoneNumber: ''
  });
  const [isLoader, setIsLoader] = useState(false);
  const [captchaValue, setCaptchaValue] = useState(null);
  const [isButtonEnabled, setIsButtonEnabled] = useState(false);
  const [countryCode, setCountryCode] = useState('');
  const [phoneTouched, setPhoneTouched] = useState(false);

  const nameInputRef = useRef(null);
  const countryInputRef = useRef();
  const { country, countrysite, state, packageId } = useParams();

  const getCountryCodeByName = (countryName) => {
    if (!countryName) {
      return '';
    }
    const country = countryData.find(
      (item) => item.label.toLowerCase() === countryName.toLowerCase()
    );
    return country ? country.dialCode : '';
  };

  const getCountryListApi = async () => {
    try {
      const searchCountryRes = await api.get(
        `${BASE_URL}${API_URL.SEARCH_COUNTRY}?country=${formValues.country}`
      );
      const { data, message, status } = searchCountryRes;
      if (status) {
        const convertedCountries = data.map((item) => ({
          label: item.countryName,
          dialCode: item.countryCode
        }));
        setCountryData(convertedCountries);
      } else {
        toast.error(message, { autoClose: 3000 });
      }
    } catch (err) {
      toast.error(err.message, { autoClose: 3000 });
    } finally {
      countryInputRef.current.focus();
    }
  };

  useEffect(() => {
    if (formValues.country.length > 1) {
      getCountryListApi();
    } else {
      setFilteredCities([]);
      setIsDropdownOpen(false);
    }
  }, [formValues.country]);

  useEffect(() => {
    if (open && nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, [open]);

  useEffect(() => {
    if (formValues.country.length > 2 && countryData.length > 0) {
      const matchedCountryCode = getCountryCodeByName(formValues.country);
      if (matchedCountryCode) {
        setFormValues((prevValues) => ({
          ...prevValues,
          phoneNumber: `+${matchedCountryCode}`
        }));
      }
    }
  }, [formValues.country, countryData]);

  const handleInputChange = (e) => {
    const key = e.target.id || e.target.name; // <-- IMPORTANT FIX
    const { value } = e.target;

    setFormValues((prevValues) => ({
      ...prevValues,
      [key]: value
    }));

    if (key === 'country' && value.length > 1) {
      const filtered = countryData.filter((country) =>
        country.label.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredCities(filtered);
      setIsDropdownOpen(filtered.length > 0);
      validateInput(key, value);
    } else {
      setFilteredCities([]);
      setIsDropdownOpen(false);
    }

    validateInput(key, value);
  };

  const handleCountrySelect = (country) => {
    setFormValues((prevValues) => ({ ...prevValues, country: country.label }));
    setErrors((prevErrors) => ({
      ...prevErrors,
      country: ''
    }));
    setIsDropdownOpen(false);
  };

  const handleBlur = () => {
    setTimeout(() => {
      setIsDropdownOpen(false);
    }, 100);
  };

  const validateInput = (field, value) => {
    let error = '';
    switch (field) {
      case 'name':
        if (value.length > 50) {
          error = 'Name cannot exceed 50 characters';
        } else if (!/^[a-zA-Z\s]*$/.test(value)) {
          error = 'Name only accepts letters and spaces';
        }
        break;
      case 'nationality':
        if (!value) {
          error = 'Please select a nationality';
        } else if (value !== 'Indian' && value !== 'NRI') {
          error = 'Select either Indian or NRI';
        } else {
          error = '';
        }
        break;
      case 'country':
        if (value.length > 50) {
          error = 'Country cannot exceed 50 characters';
        } else if (
          !countryData.some((country) => country.label.toLowerCase() === value.toLowerCase())
        ) {
          error = 'Enter valid country name';
        }
        break;
      case 'email': {
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (value.length > 50) {
          error = 'Email cannot exceed 50 characters';
        } else if (!emailRegex.test(value)) {
          error = 'Please enter a valid email address';
        }
        break;
      }
      case 'phoneNumber':
        if (!/^(\+?\d{1,15})$/.test(value)) {
          error =
            'Phone number must be between 10 and 15 characters and only contain numbers and +';
        } else if (value.length < 10 || value.length > 15) {
          error = 'Phone number must be between 10 and 15 characters';
        }
        break;

      default:
        break;
    }
    setErrors((prevErrors) => ({
      ...prevErrors,
      [field]: error
    }));
  };

  const isFormValid = () =>
    Object.values(formValues).every((field) => field) &&
    !Object.values(errors).some((error) => error);

  const onCaptchaChange = (value) => {
    setCaptchaValue(value);
    setIsButtonEnabled(!!value);
  };

  const handlePhoneChange = (value, country) => {
    setPhoneTouched(true);
    setFormValues((prevValues) => ({
      ...prevValues,
      phoneNumber: value
    }));

    setCountryCode(country?.country?.dialCode);
    const phoneWithoutCountryCode = value
      .replace(`+${country?.country?.dialCode}`, '')
      .replace(/[^\d]/g, '');

    if (phoneTouched) {
      if (phoneWithoutCountryCode.length < 7 || phoneWithoutCountryCode.length > 15) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          phoneNumber: 'Phone number must be between 10 and 15 characters'
        }));
      } else {
        setErrors((prevErrors) => ({
          ...prevErrors,
          phoneNumber: ''
        }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!captchaValue) {
      toast.error('Please verify that you are not a robot!', { autoClose: 3000 });
      return;
    }
    if (!isFormValid()) return;
    if (
      !formValues.nationality ||
      (formValues.nationality !== 'Indian' && formValues.nationality !== 'NRI')
    ) {
      setErrors((prev) => ({ ...prev, nationality: 'Please select either Indian or NRI' }));
    }
    if (
      !countryData.some(
        (country) => country.label.toLowerCase() === formValues.country.toLowerCase()
      )
    ) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        country: 'Enter valid country name'
      }));
      return;
    }

    setIsLoader(true);
    try {
      const existingTripDetails = JSON.parse(sessionStorage.getItem('tripDetails')) || {};
      let campaignDetails = {};

      try {
        campaignDetails = JSON.parse(sessionStorage.getItem('campaign_details')) || {};
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error parsing campaign_details:', error);
        campaignDetails = {};
      }
      const updatedTripDetails = {
        ...existingTripDetails,
        userDetails: formValues
      };
      sessionStorage.setItem('tripDetails', JSON.stringify(updatedTripDetails));

      const {
        packageId,
        countryName,
        tripMonth,
        tripDate,
        vacationGroup,
        vacationDays,
        tripMembers = {}
      } = existingTripDetails;

      // Create an Enquiry payload
      const enquiryData = {
        name: formValues.name,
        country_code: countryCode,
        mobile: formValues.phoneNumber.replace(`+${countryCode}`, ''),
        email: formValues.email,
        nationality: formValues.nationality,
        country: formValues.country,
        destination: packageId === 'N/A' ? 'N/A' : countryName,
        package_id: packageId === 'N/A' ? 'N/A' : packageId,
        month: tripMonth,
        date_of_travel: tripDate === 'N/A' ? 'N/A' : moment(tripDate).format('YYYY-MM-DD'),
        travelling_with: vacationGroup,
        duration: vacationDays,
        adults: Number(tripMembers?.adults),
        children: Number(tripMembers?.children),
        infants: Number(tripMembers?.infants),
        hotel_rating: Number(tripMembers?.hotelRating),
        include_flights: tripMembers?.includeFlights,
        transportation_type: tripMembers?.transportationType,
        campaign_details: campaignDetails
      };

      // API integration
      const enquiryRes = await api.post(`${BASE_URL}${API_URL.CREATE_ENQUIRY}`, enquiryData);
      const { status, data } = enquiryRes;
      if (status) {
        toast.success(data, { autoClose: 3000 });
        handleCloseWithReset();
        // navigate('/trip-confirmation');
        navigate(`/explore/${country}/${countrysite}/${state}/${packageId}/trip-confirmation`);

        // handleOtpModal();
      } else {
        toast.error(data, { autoClose: 3000 });
      }
    } catch (error) {
      toast.error(error.message, { autoClose: 3000 });
    } finally {
      setIsLoader(false);
    }
  };

  const handleCloseWithReset = () => {
    setFormValues({
      name: '',
      email: '',
      phoneNumber: '',
      nationality: '',
      country: ''
    });
    setErrors({
      name: '',
      email: '',
      phoneNumber: '',
      nationality: '',
      country: ''
    });
    setCountryData([]);
    setFilteredCities([]);
    setIsDropdownOpen(false);
    setPhoneTouched(false);
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
        <TextField
          autoComplete="off"
          autoFocus
          inputRef={nameInputRef}
          margin="dense"
          id="name"
          label="Name"
          type="text"
          fullWidth
          variant="outlined"
          value={formValues.name}
          onChange={handleInputChange}
          required
          inputProps={{ maxLength: 50 }}
          helperText={errors.name}
          error={!!errors.name}
          disabled={isLoader}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
        <TextField
          autoComplete="off"
          margin="dense"
          id="email"
          label="Email Id"
          type="email"
          fullWidth
          variant="outlined"
          value={formValues.email}
          onChange={handleInputChange}
          required
          inputProps={{ maxLength: 50 }}
          helperText={errors.email}
          error={!!errors.email}
          disabled={isLoader}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={6} xl={6} sx={{ zIndex: 1000 }}>
        <FormControl
          fullWidth
          margin="dense"
          variant="outlined"
          error={!!errors.nationality}
          disabled={isLoader}>
          <InputLabel id="nationality-label">Select Nationality</InputLabel>
          <Select
            labelId="nationality-label"
            id="nationality"
            value={formValues.nationality || ''} // ✅ ensure controlled component
            onChange={
              (e) => setFormValues({ ...formValues, nationality: e.target.value }) // ✅ updates state
            }
            label="Select Nationality"
            required
            MenuProps={{
              PaperProps: {
                style: {
                  height: 'unset',
                  maxHeight: '120px', // ✅ dropdown visible without scroll
                  overflowY: 'auto',
                  WebkitOverflowScrolling: 'touch',
                  backgroundColor: '#fff',
                  zIndex: 2000
                }
              }
            }}
            sx={{
              '& .MuiSelect-icon': {
                transition: 'transform 0.2s'
              }
            }}>
            <MenuItem disabled value="">
              Select Nationality
            </MenuItem>
            <MenuItem value="Indian">Indian</MenuItem>
            <MenuItem value="NRI">NRI</MenuItem>
          </Select>
          <FormHelperText>{errors.nationality}</FormHelperText>
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
        <Box style={{ position: 'relative' }}>
          <TextField
            autoComplete="off"
            margin="dense"
            id="country"
            label="Country"
            type="text"
            fullWidth
            variant="outlined"
            name="country"
            value={formValues.country}
            onChange={handleInputChange}
            required
            inputRef={countryInputRef}
            helperText={errors.country}
            error={!!errors.country}
            disabled={isLoader}
            onBlur={handleBlur}
          />
          {isDropdownOpen && (
            <ul style={dropdownStyles}>
              {filteredCities.map((country) => (
                <li
                  key={country.label}
                  onMouseDown={() => handleCountrySelect(country)}
                  style={listItemStyles}>
                  {country.label}
                </li>
              ))}
            </ul>
          )}
        </Box>
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={6} xl={6} mt={1}>
        <PhoneInput
          className="user-details-phone-country-btn"
          value={formValues.phoneNumber}
          onChange={handlePhoneChange}
          countryCode={countryCode}
          inputProps={{ maxLength: 15 }}
          fullWidth
          inputStyle={{
            width: '330px',
            height: '56px',
            fontSize: '16px',
            borderRadius: '4px',
            borderColor: 'rgba(0, 0, 0, 0.23)',
            borderWidth: '1px',
            borderStyle: 'solid',
            boxSizing: 'border-box'
          }}
        />
        {phoneTouched && errors.phoneNumber && (
          <Box style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
            {errors.phoneNumber}
          </Box>
        )}
      </Grid>
      <Grid
        item
        xs={12}
        sm={12}
        md={12}
        lg={12}
        xl={12}
        container
        justifyContent={{ xs: 'center', md: 'center' }}
        alignItems="center"
        sx={{
          display: 'flex',
          justifyContent: { md: 'center', lg: 'center', xl: 'center' },
          marginTop: 2
        }}>
        <Box
          sx={{
            width: { xs: '100%', md: 'auto' },
            display: 'flex',
            justifyContent: 'center'
          }}>
          <ReCAPTCHA sitekey={process.env.REACT_APP_SITE_KEY} onChange={onCaptchaChange} />
        </Box>
      </Grid>

      <Grid
        item
        xs={12}
        sm={12}
        md={6}
        lg={3}
        xl={3}
        sx={{ display: { xs: 'none', sm: 'none', md: 'block' } }}
      />
      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
        <Grid container justifyContent="center">
          <Button
            type="submit"
            variant="contained"
            color="error"
            fullWidth
            disabled={isLoader || !isButtonEnabled}
            onClick={(e) => {
              if (!isLoader) {
                handleSubmit(e);
              }
            }}
            sx={{
              fontFamily: 'gothamBold',
              backgroundColor: isFormValid() ? '#d32f2f' : '#d32f2f',
              color: isFormValid() ? '#fff' : '#fff',
              '&:disabled': {
                backgroundColor: '#d32f2f',
                color: '#fff',
                opacity: 0.5
              }
            }}>
            {isLoader ? (
              <CircularProgress size={24} style={{ color: '#1a90ff' }} />
            ) : (
              'Submit Details'
            )}
          </Button>
        </Grid>
      </Grid>
      <Grid
        item
        xs={12}
        sm={12}
        md={6}
        lg={3}
        xl={3}
        sx={{ display: { xs: 'none', sm: 'none', md: 'block' } }}
      />
    </Grid>
  );
};

export default UserDetailsModal;
